import { useTranslationWithNamespace } from '@gov-nx/core/service';
import type { DeepValueOf, FilterParam, Leaves } from '@gov-nx/core/types';
import { isString, splitByDot } from '@gov-nx/core/types';
import { toDate, toDateReadable, useFilter } from '@gov-nx/utils/common';
import type { PageCode } from '../../definitions/codes';
import type { RegisterSVFilterData, RegistrSVFormData } from './context.types';

type FilterPaths =
	| keyof Pick<
			RegistrSVFormData,
			'filterText' | 'datumOd' | 'datumDo' | 'typOsoby'
	  >
	| Leaves<Pick<RegistrSVFormData, 'filterStatus' | 'filterVztah'>>;

interface FilterParamsProps {
	code: PageCode;
	defaultValues: RegistrSVFormData;
	onFilterRemove: (
		key: FilterPaths,
		value: DeepValueOf<RegisterSVFilterData>
	) => void;
}

export const useFilters = ({
	code,
	defaultValues,
	onFilterRemove,
}: FilterParamsProps) => {
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tsn = getLocalizeCurried(code);
	const { filter, setPartialFilter, setPartialFilterWithPath } =
		useFilter<RegistrSVFormData>({
			key: code,
			initialState: defaultValues,
			rehydrate: (data) => ({
				...data,
				datumOd: isString(data.datumOd)
					? (toDate(data.datumOd) ?? null)
					: data.datumOd,
				datumDo: isString(data.datumDo)
					? (toDate(data.datumDo) ?? null)
					: data.datumDo,
			}),
		});

	const onRemove = (path: FilterPaths) => () => {
		const [name, key] = splitByDot(path);
		switch (name) {
			case 'typOsoby':
			case 'filterText':
			case 'datumOd':
			case 'datumDo':
				setPartialFilter({ [name]: defaultValues[name] });
				onFilterRemove(name, defaultValues[name]);
				break;
			case 'filterStatus':
				setPartialFilterWithPath({ [key]: defaultValues[name][key] }, name);
				onFilterRemove(path, defaultValues[name][key]);
				break;
			case 'filterVztah':
				setPartialFilterWithPath({ [key]: defaultValues[name][key] }, name);
				onFilterRemove(path, defaultValues[name][key]);
				break;
		}
	};

	const filterParams: FilterParam[] = [
		{
			label: tsn('formular.label.text-filtr'),
			value: filter.filterText ?? undefined,
			onRemove: onRemove('filterText'),
			isActive: !!filter.filterText,
		},
		{
			label: tsn('formular.label.status'),
			value: tsn('formular.pole.filter-status-provozovane'),
			onRemove: onRemove('filterStatus.provozovane'),
			isActive: filter.filterStatus.provozovane,
		},
		{
			label: tsn('formular.label.status'),
			value: tsn('formular.pole.filter-status-zanik'),
			onRemove: onRemove('filterStatus.zanik'),
			isActive: filter.filterStatus.zanik,
		},
		{
			label: tsn('formular.label.vztah-k-vozidlu'),
			value: tsn('formular.pole.filtr-vztah-vlastnik'),
			onRemove: onRemove('filterVztah.vlastnik'),
			isActive: filter.filterVztah.vlastnik,
		},
		{
			label: tsn('formular.label.vztah-k-vozidlu'),
			value: tsn('formular.pole.filtr-vztah-provozovatel'),
			onRemove: onRemove('filterVztah.provozovatel'),
			isActive: filter.filterVztah.provozovatel,
		},
		{
			label: tsn('formular.label.obdobi-od'),
			value: filter.datumOd ? toDateReadable(filter.datumOd) : undefined,
			onRemove: onRemove('datumOd'),
			isActive: !!filter.datumOd,
		},
		{
			label: tsn('formular.label.obdobi-do'),
			value: filter.datumDo ? toDateReadable(filter.datumDo) : undefined,
			onRemove: onRemove('datumDo'),
			isActive: !!filter.datumDo,
		},
		{
			label: tsn('formular.label.typOsoby'),
			value: tsn('formular.pole.typ-osoby-pfo'),
			onRemove: onRemove('typOsoby'),
			isActive: filter.typOsoby === 'PFO',
		},
	];

	return { filterParams, filter, setPartialFilter };
};
